import React, { FC, memo, useCallback, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";

import { LoadingSpinner } from "@/components/Loading";
import { Section } from "@/components/Section";
import { UserContext } from "@/components/User";
import { MEDIA_SIZE_SM } from "@/core/media";
import { IURLSortConfig } from "@/core/url";
import {
  fetchWorkOrderLifecycleList,
  IPageableWorkOrderLifecycle,
  IWorkOrder,
  IWorkOrderLifecycle,
} from "@/data/work-orders";
import { Flex } from "@/eworkitComponents/components";
import { TPageableFetchFunction, usePageableFetcher } from "@/hooks/http";

import { NoRecords } from "./NoRecords";
import { WorkOrderDetailsLifecycleList } from "./WorkOrderDetailsLifecycleList";
import { WorkOrderDetailsLifecycleTable } from "./WorkOrderDetailsLifecycleTable";

export interface IWorkOrderDetailsLifecycleProps {
  workOrder: IWorkOrder;
}

export const WorkOrderDetailsLifecycle: FC<IWorkOrderDetailsLifecycleProps> = memo(({ workOrder }) => {
  const { t } = useTranslation("session");
  const { contextCompany } = useContext(UserContext);
  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const fetchWorkOrderLifecycle: TPageableFetchFunction<
    IWorkOrderLifecycle,
    IPageableWorkOrderLifecycle,
    any,
    IURLSortConfig[]
  > = useCallback(
    (params, config) => {
      return fetchWorkOrderLifecycleList(
        { companyId: contextCompany?.id, workOrderId: workOrder.id, filtersConfig: { size: 100 }, ...params },
        config,
      );
    },
    [contextCompany?.id, workOrder.id],
  );

  const { payload: lifecyclesPayload, working } = usePageableFetcher(fetchWorkOrderLifecycle);
  const lifecycles = lifecyclesPayload?.content.map(lifecycle => lifecycle.data) ?? [];

  return (
    <Section
      width={12}
      direction="column"
      shadow={isSmallScreen ? "none" : "default"}
      borderRadius={!isSmallScreen}
      padding={["lg"]}
      innerBorder>
      {working && (
        <Flex margin={["xxl", false]} align="center" justify="center">
          <LoadingSpinner size="lg" variant="primary" />
        </Flex>
      )}

      {!working && !lifecycles.length && <NoRecords>{t("WORK_ORDERS.DETAILS.LIFECYCLE_DETAILS.NO_RECORDS")}</NoRecords>}

      {!working && !!lifecycles.length && (
        <>
          {!isSmallScreen && <WorkOrderDetailsLifecycleTable lifecycles={lifecycles} />}

          {isSmallScreen && <WorkOrderDetailsLifecycleList lifecycles={lifecycles} />}
        </>
      )}
    </Section>
  );
});
